import React from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';

import TreeSelection from '@adsk/bim360-matrix-react-tree-selection';
import DropdownMenu from '@adsk/bim360-matrix-react-dropdown-menu';
import Button from '@adsk/bim360-matrix-react-button';
import Loader from '@adsk/bim360-matrix-react-loader';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import styles from './styles';

import './_Tree.scss';

export class Tree extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentSelectedNode: props.currentSelectedNode,
      maxHeight: 0,
      width: 0
    };

    this._handleChange = this._handleChange.bind(this);
    this.setContainer = this.setContainer.bind(this);
    this.calculateDimensions = this.calculateDimensions.bind(this);
  }

  calculateDimensions() {
    if (!this.container) {
      return;
    }
    const computedStyle = getComputedStyle(this.container);

    const maxHeight = parseInt(computedStyle.maxHeight, 10);
    const width = parseInt(computedStyle.width, 10);
    this.setState({ maxHeight, width });
  }

  componentDidMount() {
    this.calculateDimensions();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.currentSelectedNode !== this.props.currentSelectedNode) {
      this.setState({ currentSelectedNode: nextProps.currentSelectedNode });
    }
  }

  _handleChange(node) {
    this.setState({ currentSelectedNode: node });
    this.props.onChange(node);
  }

  setContainer(ref) {
    this.container = ref;
    this.calculateDimensions();
  }

  renderTree() {
    return (
      <DropdownMenu>
        <li className="Tree__tree-container" ref={this.setContainer}>
          {this.state.maxHeight ? (
            <TreeSelection
              reactTreebeardStyle={styles}
              treeData={this.props.treeData}
              onChange={this._handleChange}
              selectedNodeId={this.state.currentSelectedNode && this.state.currentSelectedNode.id}
              locale={this.props.locale}
              onlyLeafSelection={this.props.onlyLeafSelection}
              fixedSize={true}
              maxHeight={this.state.maxHeight}
              width={this.state.width}
            />
          ) : null}
        </li>
        <li>
          <div className="Tree__divider" />
          <div className="Tree__buttons">
            <Button
              styleType={Button.StyleType.LINK_SECONDARY}
              className="Tree__cancel"
              onClick={this.props.onCancel}
            >
              {this.props[TSymbolProp].translate('CANCEL')}
            </Button>
            {this.props.clearable && (
              <Button
                styleType={Button.StyleType.LINK}
                className="Tree__clear"
                disabled={!this.state.currentSelectedNode}
                onClick={() => {
                  this._handleChange(null);
                }}
              >
                {this.props[TSymbolProp].translate('CLEAR')}
              </Button>
            )}
          </div>
        </li>
      </DropdownMenu>
    );
  }

  renderTryAgain() {
    if (!this.props.onTryAgain) {
      return null;
    }
    return (
      <Button
        styleType={Button.StyleType.INLINE_LINK}
        className="Tree__try-again"
        onClick={this.props.onTryAgain}
      >
        {this.props[TSymbolProp].translate('TRY_AGAIN')}
      </Button>
    );
  }

  renderErrorState() {
    return (
      <li className="Tree__error-message">
        <span className="Tree__error-message-text">
          {this.props[TSymbolProp].translate('FAILED_TO_LOAD_LOCATIONS')}
        </span>
        {this.renderTryAgain()}
      </li>
    );
  }

  renderEmptyState() {
    return (
      <li className="Tree__empty-state">{this.props[TSymbolProp].translate('NO_RESULTS_FOUND')}</li>
    );
  }

  renderLoader() {
    return (
      <li className="Tree__loading">
        <Loader className="Tree__loading-loader" />
      </li>
    );
  }

  renderMessage() {
    return (
      <DropdownMenu>
        {this.props.isLoading
          ? this.renderLoader()
          : this.props.showErrorMessage
            ? this.renderErrorState()
            : this.renderEmptyState()}
      </DropdownMenu>
    );
  }

  render() {
    return (
      <div className="Tree">{this.props.treeData ? this.renderTree() : this.renderMessage()}</div>
    );
  }
}

const treeNodeShape = PropTypes.shape({
  /**
   * Tree node id
   */
  id: PropTypes.string.isRequired,
  /**
   * Tree node display name
   */
  name: PropTypes.string.isRequired,
  /**
   * Array of tree node children
   */
  children: PropTypes.array
});

Tree.defaultProps = {
  onChange: noop,
  onCancel: noop,
  onlyLeafSelection: false,
  clearable: true
};

Tree.propTypes = {
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  treeData: PropTypes.oneOfType([PropTypes.arrayOf(treeNodeShape), treeNodeShape]),
  currentSelectedNode: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    toggled: PropTypes.bool
  }),
  locale: PropTypes.string,
  showErrorMessage: PropTypes.bool,
  isLoading: PropTypes.bool,
  onTryAgain: PropTypes.func,
  onlyLeafSelection: PropTypes.bool,
  clearable: PropTypes.bool
};

export default withLocale(Tree, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
